import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import { FaPlus, FaTrash } from 'react-icons/fa';
import { Button, Card, Col, Row } from 'react-bootstrap';
import AppContext from 'context/Context';
import { FormControl, InputLabel, MenuItem, Select, TextField, IconButton } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';

const ProductoCard = (
    {
        producto,
        index,
        actualizarCampo,
        eliminarProducto,
    }
) => {
    const { config: { isDark } } = useContext(AppContext);

    /**
     * Calculos de productos
     */
    const imagenSrc = producto.vImagenProducto_Base64 
        ? `data:image/jpeg;base64,${producto.vImagenProducto_Base64}` 
        : "";

    const precioUnitario = producto.dPrecioUnitario || 0;
    const cantidad = producto.dCantidad || 1;
    const importe = producto.dImporte || 0;
    const total = producto.dTotal || 0;
    const impuestos = producto.impuestos || [];

    // Estilos personalizados para inputs de Material-UI en modo claro/oscuro
    const muiInputStyles = {
        '& .MuiOutlinedInput-root': {
            backgroundColor: isDark ? '#121416' : '#fff',
            color: isDark ? '#fff' : '#000',
            '& fieldset': {
                borderColor: isDark ? '#3b424a' : '#d8e2ef',
            },
            '&:hover fieldset': {
                borderColor: isDark ? '#4b5563' : '#a2b4cd',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#009bdb',
            },
            '&.Mui-disabled fieldset': {
                borderColor: isDark ? '#2a2d31' : '#e0e0e0',
            },
            '& input': {
                color: isDark ? '#fff !important' : '#000 !important',
                WebkitTextFillColor: isDark ? '#fff !important' : '#000 !important',
            },
            '&.Mui-disabled input': {
                color: isDark ? '#7f8c8d !important' : '#a0a0a0 !important',
                WebkitTextFillColor: isDark ? '#7f8c8d !important' : '#a0a0a0 !important',
            }
        },
        '& .MuiInputLabel-root': {
            color: isDark ? '#e4e6eb !important' : '#2c3e50 !important',
            '&.Mui-focused': {
                color: '#009bdb !important',
            },
            '&.Mui-disabled': {
                color: isDark ? '#7f8c8d !important' : '#a0a0a0 !important',
                WebkitTextFillColor: isDark ? '#7f8c8d !important' : '#a0a0a0 !important',
            }
        },
        '& .MuiSelect-icon': {
            color: isDark ? '#e4e6eb !important' : '#2c3e50 !important',
        },
        '& .MuiSelect-select': {
            color: isDark ? '#fff !important' : '#000 !important',
            '&.Mui-disabled': {
                color: isDark ? '#7f8c8d !important' : '#a0a0a0 !important',
                WebkitTextFillColor: isDark ? '#7f8c8d !important' : '#a0a0a0 !important',
            }
        }
    };

    const tieneIVATrasladado = (listaImpuestos) => {
        return listaImpuestos.some(imp => imp.tipo === "Traslado" && imp.impuesto === "002");
    };

    const limpiarIVARetenido = (listaImpuestos) => {
        if (!tieneIVATrasladado(listaImpuestos)) {
            // Eliminar Retención de IVA si no hay IVA Trasladado
            return listaImpuestos.filter(imp => !(imp.tipo === "Retencion" && imp.impuesto === "002"));
        }
        return listaImpuestos;
    };

    const actualizarImpuesto = (idImpuesto, campo, valor) => {
        let nuevosImpuestos = impuestos.map(imp => {
            if (imp.id === idImpuesto) {
                let nuevoImp = { ...imp, [campo]: valor };
                
                // Si se cambia el tipo de impuesto, asignar una tasa común sugerida
                if (campo === 'impuesto') {
                    if (valor === '001') nuevoImp.tasaCuota = 0.100000; // ISR (común: 10% o 1.25%)
                    if (valor === '002' && imp.tipo === 'Retencion') nuevoImp.tasaCuota = 0.106666; // IVA Retenido
                    if (valor === '002' && imp.tipo === 'Traslado') nuevoImp.tasaCuota = 0.160000; // IVA Trasladado
                    if (valor === '003') nuevoImp.tasaCuota = 0.080000; // IEPS
                }
                
                return nuevoImp;
            }
            return imp;
        });
        
        if (campo === 'tipo' || campo === 'impuesto') {
            nuevosImpuestos = limpiarIVARetenido(nuevosImpuestos);
        }
        
        actualizarCampo(producto.idUnica, 'impuestos', nuevosImpuestos);
    };

    const agregarTraslado = () => {
        const nuevosImpuestos = [
            ...impuestos,
            {
                id: uuidv4(),
                tipo: "Traslado",
                impuesto: "002",
                tipoFactor: "Tasa",
                tasaCuota: 0.160000,
                importe: 0 // Se calculará en el contenedor
            }
        ];
        actualizarCampo(producto.idUnica, 'impuestos', nuevosImpuestos);
    };

    const agregarRetencion = () => {
        const nuevosImpuestos = [
            ...impuestos,
            {
                id: uuidv4(),
                tipo: "Retencion",
                impuesto: "001",
                tipoFactor: "Tasa",
                tasaCuota: 0.100000,
                importe: 0 // Se calculará en el contenedor
            }
        ];
        actualizarCampo(producto.idUnica, 'impuestos', nuevosImpuestos);
    };

    const eliminarImpuesto = (idImpuesto) => {
        let nuevosImpuestos = impuestos.filter(imp => imp.id !== idImpuesto);
        nuevosImpuestos = limpiarIVARetenido(nuevosImpuestos);
        actualizarCampo(producto.idUnica, 'impuestos', nuevosImpuestos);
    };


    return (
        <Card
            key={index}
            className="mb-3 border border-200 shadow-sm"
            style={{
                borderRadius: '8px',
            }}
        >
            {/* Barra superior */}
            <div
                className="bg-primary"
                style={{
                    height: '3px',
                    borderRadius: '8px 8px 0 0',
                }}
            />

            <Card.Body className="p-3">
                {/* Header con imagen, título y botón eliminar */}
                <Row className="align-items-center">
                    {/* Imagen del producto */}
                    <Col xs={3} sm={2} md={1} className="text-center pe-0 d-none d-sm-block">
                        <div
                            className="border border-200 d-flex align-items-center justify-content-center bg-200 text-600"
                            style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '8px',
                                overflow: 'hidden',
                            }}
                        >
                            {imagenSrc ? (
                                <img
                                    src={imagenSrc}
                                    alt="Producto"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                            ) : (
                                <span className="fs-1 fw-bold">
                                    {producto.vNombreProducto ? producto.vNombreProducto.substring(0, 2).toUpperCase() : 'NA'}
                                </span>
                            )}
                        </div>
                    </Col>

                    {/* Información del producto */}
                    <Col xs={12} sm={8} md={10}>
                        <Row>
                            <Col xs={12} sm={6} md={4} className="mb-3">
                                <TextField
                                    label="Código de Barras / No. Identificación"
                                    size="small"
                                    value={producto.vCodigoBarras}
                                    onChange={(e) => {
                                        actualizarCampo(producto.idUnica, 'vCodigoBarras', e.target.value);
                                    }}
                                    fullWidth
                                    inputProps={{
                                        style: { fontSize: '0.7rem' }
                                    }}
                                    sx={muiInputStyles}
                                />
                            </Col>
                            <Col xs={12} sm={6} md={4} className="mb-3">
                                <TextField
                                    label="Clave Unidad"
                                    size="small"
                                    value={producto.vClaveUnidadSat}
                                    onChange={(e) => {
                                        actualizarCampo(producto.idUnica, 'vClaveUnidadSat', e.target.value);
                                    }}
                                    fullWidth
                                    inputProps={{
                                        style: { fontSize: '0.85rem' }
                                    }}
                                    sx={muiInputStyles}
                                />
                            </Col>
                            <Col xs={12} sm={6} md={4} className="mb-3">
                                <TextField
                                    label="Código SAT"
                                    size="small"
                                    value={producto.vCodigoProductoServicioSat}
                                    onChange={(e) => {
                                        actualizarCampo(producto.idUnica, 'vCodigoProductoServicioSat', e.target.value);
                                    }}
                                    fullWidth
                                    inputProps={{
                                        style: { fontSize: '0.85rem' }
                                    }}
                                    sx={muiInputStyles}
                                />
                            </Col>
                        </Row>
                        <Row className="mb-1">
                            <Col xs={12}>
                                <TextField
                                    label="Descripción"
                                    size="small"
                                    value={producto.vNombreProducto}
                                    onChange={(e) => {
                                        actualizarCampo(producto.idUnica, 'vNombreProducto', e.target.value);
                                    }}
                                    fullWidth
                                    multiline
                                    rows={2}
                                    inputProps={{
                                        style: { fontSize: '0.95rem', fontWeight: 'bold' }
                                    }}
                                    sx={muiInputStyles}
                                />
                            </Col>
                        </Row>
                    </Col>

                    {/* Botón eliminar */}
                    <Col xs={0} sm={2} md={1} className="text-end ps-0">
                        <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => eliminarProducto(index)}
                            className="d-none d-sm-block"
                            style={{
                                borderRadius: '6px',
                                width: '36px',
                                height: '36px',
                                padding: '0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <FaTrash size={14} />
                        </Button>
                    </Col>
                </Row>

                {/* Divisor */}
                <hr className="my-2 border-200" />

                {/* Cantidad y Precios */}
                <Row className="g-2 mb-3">
                    <Col xs={12} sm={4} md={4}>
                        <TextField
                            label="Cant."
                            type="number"
                            size="small"
                            value={cantidad}
                            onChange={(e) => actualizarCampo(producto.idUnica, 'dCantidad', parseFloat(e.target.value) || 0)}
                            fullWidth
                            inputProps={{
                                min: 0,
                                step: "any",
                                style: { fontSize: '0.85rem' }
                            }}
                            sx={muiInputStyles}
                        />
                    </Col>
                    <Col xs={12} sm={4} md={4}>
                        <TextField
                            label="Precio Unitario"
                            type="number"
                            size="small"
                            value={precioUnitario}
                            onChange={(e) => actualizarCampo(producto.idUnica, 'dPrecioUnitario', parseFloat(e.target.value) || 0)}
                            fullWidth
                            inputProps={{
                                min: 0,
                                step: "any",
                                style: { fontSize: '0.9rem', fontWeight: '500' }
                            }}
                            sx={muiInputStyles}
                        />
                    </Col>
                    <Col xs={12} sm={4} md={4}>
                        <TextField
                            label="Importe Base"
                            type="number"
                            size="small"
                            value={importe.toFixed(2)}
                            fullWidth
                            inputProps={{
                                readOnly: true,
                                style: {
                                    fontSize: '0.9rem',
                                    fontWeight: '600',
                                    color: 'inherit',
                                },
                            }}
                            sx={muiInputStyles}
                        />
                    </Col>
                </Row>

                {/* Subsección de Impuestos */}
                <div className="bg-light p-2 rounded border mb-2">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="fw-bold fs--1 text-600">Impuestos (Traslados y Retenciones)</span>
                        <div>
                            <Button variant="outline-secondary" size="sm" onClick={agregarTraslado} className="me-2" style={{ fontSize: '0.75rem', padding: '0.1rem 0.5rem' }}>
                                <FaPlus className="me-1" /> Agregar Traslado
                            </Button>
                            <Button variant="outline-secondary" size="sm" onClick={agregarRetencion} style={{ fontSize: '0.75rem', padding: '0.1rem 0.5rem' }}>
                                <FaPlus className="me-1" /> Agregar Retención
                            </Button>
                        </div>
                    </div>

                    {impuestos.length === 0 && (
                        <div className="text-muted fs--2 fst-italic">No hay impuestos configurados.</div>
                    )}

                    {impuestos.map((imp) => (
                        <Row key={imp.id} className="g-2 mb-2 align-items-center">
                            <Col xs={12} sm={2} className="d-flex align-items-center ps-3">
                                <span className={`fw-bold ${imp.tipo === 'Traslado' ? 'text-primary' : 'text-danger'}`} style={{ fontSize: '0.85rem' }}>
                                    {imp.tipo === "Traslado" ? "TRASLADO" : "RETENCIÓN"}
                                </span>
                            </Col>
                            <Col xs={12} sm={2}>
                                <FormControl size="small" fullWidth sx={muiInputStyles}>
                                    <InputLabel id={`impuesto-label-${imp.id}`}>Impuesto</InputLabel>
                                    <Select
                                        labelId={`impuesto-label-${imp.id}`}
                                        label="Impuesto"
                                        value={imp.impuesto}
                                        onChange={(e) => actualizarImpuesto(imp.id, 'impuesto', e.target.value)}
                                        sx={{ fontSize: '0.80rem' }}
                                    >
                                        {imp.tipo === "Traslado" ? [
                                            <MenuItem key="002" value={"002"}>IVA (002)</MenuItem>,
                                            <MenuItem key="003" value={"003"}>IEPS (003)</MenuItem>
                                        ] : [
                                            <MenuItem key="001" value={"001"}>ISR (001)</MenuItem>,
                                            tieneIVATrasladado(impuestos) && <MenuItem key="002" value={"002"}>IVA (002)</MenuItem>
                                        ]}
                                    </Select>
                                </FormControl>
                            </Col>
                            <Col xs={12} sm={2}>
                                <FormControl size="small" fullWidth sx={muiInputStyles}>
                                    <InputLabel id={`factor-label-${imp.id}`}>Factor</InputLabel>
                                    <Select
                                        labelId={`factor-label-${imp.id}`}
                                        label="Factor"
                                        value={imp.tipoFactor}
                                        onChange={(e) => actualizarImpuesto(imp.id, 'tipoFactor', e.target.value)}
                                        sx={{ fontSize: '0.80rem' }}
                                    >
                                        <MenuItem value={"Tasa"}>Tasa</MenuItem>
                                        <MenuItem value={"Cuota"}>Cuota</MenuItem>
                                        <MenuItem value={"Exento"}>Exento</MenuItem>
                                    </Select>
                                </FormControl>
                            </Col>
                            <Col xs={12} sm={3}>
                                <TextField
                                    label="Tasa/Cuota"
                                    type="number"
                                    size="small"
                                    value={imp.tipoFactor === "Exento" ? 0 : imp.tasaCuota}
                                    onChange={(e) => {
                                        const nuevoValor = e.target.value;
                                        actualizarImpuesto(imp.id, 'tasaCuota', nuevoValor === "" ? "" : parseFloat(nuevoValor));
                                    }}
                                    fullWidth
                                    inputProps={{ min: 0, step: "any", style: { fontSize: '0.80rem' } }}
                                    sx={muiInputStyles}
                                    disabled={imp.tipoFactor === "Exento"}
                                />
                            </Col>
                            <Col xs={10} sm={2}>
                                <TextField
                                    label="Importe"
                                    type="number"
                                    size="small"
                                    value={(imp.importe || 0).toFixed(2)}
                                    fullWidth
                                    inputProps={{ readOnly: true, style: { fontSize: '0.80rem', fontWeight: 'bold' } }}
                                    sx={muiInputStyles}
                                />
                            </Col>
                            <Col xs={2} sm={1} className="text-end">
                                <IconButton color="error" size="small" onClick={() => eliminarImpuesto(imp.id)}>
                                    <FaTrash size={12} />
                                </IconButton>
                            </Col>
                        </Row>
                    ))}
                </div>

                <Row>
                    <Col xs={12} className="d-flex justify-content-end align-items-center">
                        <div className="bg-primary rounded-2 p-2 px-4 d-inline-block">
                            <span className="text-white fs--1 me-2">Total Concepto:</span>
                            <span className="text-white fw-bold fs-0">
                                ${total.toFixed(2)}
                            </span>
                        </div>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
};

// Definición de PropTypes
ProductoCard.propTypes = {
    producto: PropTypes.shape({
        idUnica: PropTypes.string,
        dPrecioUnitario: PropTypes.number,
        dCantidad: PropTypes.number,
        dImporte: PropTypes.number,
        dTotal: PropTypes.number,
        impuestos: PropTypes.array,
        vCodigoBarras: PropTypes.string,
        vNombreProducto: PropTypes.string,
        vClaveUnidadSat: PropTypes.string,
        vCodigoProductoServicioSat: PropTypes.string,
        imagenURL: PropTypes.string,
        vImagenProducto_Base64: PropTypes.string
    }).isRequired,
    index: PropTypes.number.isRequired,
    actualizarCampo: PropTypes.func.isRequired,
    eliminarProducto: PropTypes.func.isRequired,
};

export default ProductoCard;